import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
      } else if (response.data.status === 'expired') {
        setStatus('expired');
      } else {
        setTimeout(() => {
          setAttempts(attempts + 1);
          pollPaymentStatus();
        }, 2000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-slate-50 flex items-center justify-center p-4">
      <Card className="bg-white border border-slate-100 shadow-xl rounded-xl p-12 max-w-md text-center">
        {status === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 text-rose-600 animate-spin mx-auto mb-6" strokeWidth={1.5} />
            <h2 className="text-2xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Vérification du paiement...</h2>
            <p className="text-slate-600">Veuillez patienter pendant que nous confirmons votre paiement.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-6" strokeWidth={1.5} />
            <h2 className="text-2xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Paiement confirmé !</h2>
            <p className="text-slate-600 mb-6">Votre paiement a été traité avec succès. Les options sélectionnées seront mises en place avant votre arrivée.</p>
            <p className="text-sm text-slate-500">Vous recevrez un email de confirmation sous peu.</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-3xl">❌</span>
            </div>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Erreur</h2>
            <p className="text-slate-600 mb-6">Une erreur est survenue lors de la vérification de votre paiement.</p>
            <Button onClick={() => window.location.reload()} className="bg-rose-600 hover:bg-rose-700 text-white rounded-lg">
              Réessayer
            </Button>
          </>
        )}

        {(status === 'timeout' || status === 'expired') && (
          <>
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-3xl">⚠️</span>
            </div>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Session expirée</h2>
            <p className="text-slate-600">La session de paiement a expiré. Veuillez réessayer.</p>
          </>
        )}
      </Card>
      
      {/* Footer */}
      <footer className="absolute bottom-4 left-0 right-0">
        <div className="flex justify-center items-center gap-4 text-xs text-slate-400">
          <Link to="/politique-de-confidentialite" className="hover:text-rose-600 transition-colors">
            Politique de confidentialité
          </Link>
          <span>•</span>
          <Link to="/mentions-legales" className="hover:text-rose-600 transition-colors">
            Mentions légales
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default PaymentSuccess;
